import type { UI } from '@/v2/types';
import { useMemo } from 'react';

import type { ProcuredOffer } from '@shared/types/client/ProcuredOffer';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import getPackageOptions from '@shared/v2/utils/getPackageOptions';

import createFlightCampaignInclusion from '@/v2/utils/campaign/createFlightCampaignInclusion';

export type UsePackageOptionsProps = {
  showCampaignPackageOptionVariants: boolean;
  isCampaignAvailable: boolean;
  procuredOffer: UI.ProcuredOffer | null;
  originCode?: string;
  campaign: UI.Campaign | null;
};

const usePackageOptions = ({
  showCampaignPackageOptionVariants,
  isCampaignAvailable,
  campaign,
  procuredOffer,
  originCode,
}: UsePackageOptionsProps) => {
  const { searchQuery } = useSearchQuery();
  const title = campaign?.sashing?.flightPillText;
  const terms = campaign?.homePageHero?.termsConditions ?? undefined;

  const procuredOfferForPackageOptions = useMemo(() => {
    return (
      procuredOffer &&
      ({
        ...procuredOffer,
        travelDates: procuredOffer.travelDateRange.end,
        saleDates: procuredOffer.saleDateRange.end,
      } as ProcuredOffer)
    );
  }, [procuredOffer]);

  // Package options without campaign considered
  const standardPackageOptions = useMemo(() => {
    return getPackageOptions({
      procuredOffer: procuredOfferForPackageOptions,
      originCode,
    }).packageOptions;
  }, [originCode, procuredOfferForPackageOptions]);

  // Package options with campaign naming + inclusions
  const campaignPackageOptions = useMemo(() => {
    const flightCampaignInclusion = createFlightCampaignInclusion({
      title,
      terms,
    });

    return getPackageOptions({
      procuredOffer: procuredOfferForPackageOptions,
      originCode,
      title: `${title} Deal`,
      commonInclusions: flightCampaignInclusion
        ? [flightCampaignInclusion]
        : undefined,
    }).packageOptions;
  }, [originCode, procuredOfferForPackageOptions, terms, title]);

  const packageOptionsForSelection =
    showCampaignPackageOptionVariants || isCampaignAvailable
      ? campaignPackageOptions
      : standardPackageOptions;

  const availablePackageOptions = isCampaignAvailable
    ? campaignPackageOptions
    : standardPackageOptions;

  const selectedPackageOption = useMemo(() => {
    if (!searchQuery?.packageOption || !packageOptionsForSelection) {
      return undefined;
    }
    return packageOptionsForSelection.find(
      (packageOption) => packageOption.id === searchQuery.packageOption,
    );
  }, [packageOptionsForSelection, searchQuery?.packageOption]);

  return {
    /** Package options for the user to select in the package options section */
    packageOptionsForSelection,
    /** Package options that are actually available */
    availablePackageOptions,
    /** Selectable package option that matches the selected query param ID */
    selectedPackageOption,
  };
};

export default usePackageOptions;
